import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "h-12 bg-white border-sand focus-visible:ring-gold rounded-none text-[15px]";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#525D6B] mb-2";

export const ValuationTeaserForm = () => {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.address) {
      toast.error(t.form.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/valuation`, { ...form, preferred_language: lang });
      toast.success(t.form.successValuation);
      setForm({ name: "", email: "", phone: "", address: "" });
    } catch {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="valuation-teaser-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Input data-testid="teaser-name-input" placeholder={t.valuationTeaser.name} value={form.name} onChange={set("name")} className={inputCls} />
      <Input data-testid="teaser-email-input" type="email" placeholder={t.valuationTeaser.email} value={form.email} onChange={set("email")} className={inputCls} />
      <Input data-testid="teaser-phone-input" placeholder={t.valuationTeaser.phone} value={form.phone} onChange={set("phone")} className={inputCls} />
      <Input data-testid="teaser-address-input" placeholder={t.valuationTeaser.address} value={form.address} onChange={set("address")} className={inputCls} />
      <button
        data-testid="teaser-submit-btn"
        type="submit"
        disabled={sending}
        className="sm:col-span-2 group inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-semibold text-sm tracking-wide px-8 py-4 transition-colors duration-300"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t.valuationTeaser.cta}
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

export const ContactForm = () => {
  const { lang, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const dev = searchParams.get("development");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: dev ? "buying" : "",
    budget: "",
    message: dev ? t.contactPage.prefillDevelopment.replace("{name}", dev) : "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.form.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, interest: form.interest || "buying", preferred_language: lang });
      toast.success(t.form.success);
      setForm({ name: "", email: "", phone: "", interest: "", budget: "", message: "" });
    } catch {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>{t.contactPage.name} *</label>
          <Input data-testid="contact-name-input" value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.contactPage.email} *</label>
          <Input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.contactPage.phone}</label>
          <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t.contactPage.interest}</label>
          <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
            <SelectTrigger data-testid="contact-interest-select" className={`${inputCls} [&>span]:text-left`}>
              <SelectValue placeholder={t.contactPage.interest} />
            </SelectTrigger>
            <SelectContent>
              {["buying", "selling", "renting", "other"].map((v, i) => (
                <SelectItem key={v} value={v} data-testid={`contact-interest-${v}`}>{t.contactPage.interestOptions[i]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className={labelCls}>{t.contactPage.budget}</label>
        <Input data-testid="contact-budget-input" value={form.budget} onChange={set("budget")} className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>{t.contactPage.message} *</label>
        <Textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} className="bg-white border-sand focus-visible:ring-gold rounded-none text-[15px]" />
      </div>
      <button
        data-testid="contact-submit-btn"
        type="submit"
        disabled={sending}
        className="group w-full inline-flex items-center justify-center gap-3 bg-ink hover:bg-charcoal disabled:opacity-60 text-cream font-semibold text-sm tracking-wide px-8 py-4 transition-colors duration-300"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t.contactPage.cta}
        <ArrowRight className="w-4 h-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

export const ValuationFullForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    address: "", city: "", zip_code: "", property_type: "", bedrooms: "", bathrooms: "",
    est_sqft: "", condition: "", timeline: "", name: "", email: "", phone: "", notes: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectField = (key, options, testid) => (
    <Select value={form[key]} onValueChange={(v) => setForm({ ...form, [key]: v })}>
      <SelectTrigger data-testid={testid} className={`${inputCls} [&>span]:text-left`}>
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testid}-option-${options.indexOf(o)}`}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address || !form.name || !form.email) {
      toast.error(t.form.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/valuation`, form);
      toast.success(t.form.successValuation);
      setForm({ address: "", city: "", zip_code: "", property_type: "", bedrooms: "", bathrooms: "", est_sqft: "", condition: "", timeline: "", name: "", email: "", phone: "", notes: "" });
    } catch {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="valuation-full-form" onSubmit={submit} className="space-y-8">
      <div>
        <p className="font-serif text-xl text-ink mb-5 flex items-center gap-3"><span className="text-gold text-sm">01</span>{t.valuationPage.propertyDetails}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <label className={labelCls}>{t.valuationPage.address} *</label>
            <Input data-testid="valuation-address-input" value={form.address} onChange={set("address")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.city}</label>
            <Input data-testid="valuation-city-input" value={form.city} onChange={set("city")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.zip}</label>
            <Input data-testid="valuation-zip-input" value={form.zip_code} onChange={set("zip_code")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.type}</label>
            {selectField("property_type", t.valuationPage.typeOptions, "valuation-type-select")}
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.condition}</label>
            {selectField("condition", t.valuationPage.conditionOptions, "valuation-condition-select")}
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.beds}</label>
            <Input data-testid="valuation-beds-input" value={form.bedrooms} onChange={set("bedrooms")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.baths}</label>
            <Input data-testid="valuation-baths-input" value={form.bathrooms} onChange={set("bathrooms")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.sqft}</label>
            <Input data-testid="valuation-sqft-input" value={form.est_sqft} onChange={set("est_sqft")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.timeline}</label>
            {selectField("timeline", t.valuationPage.timelineOptions, "valuation-timeline-select")}
          </div>
        </div>
      </div>

      <div>
        <p className="font-serif text-xl text-ink mb-5 flex items-center gap-3"><span className="text-gold text-sm">02</span>{t.valuationPage.contactInfo}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>{t.valuationPage.name} *</label>
            <Input data-testid="valuation-name-input" value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.email} *</label>
            <Input data-testid="valuation-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.valuationPage.phone}</label>
            <Input data-testid="valuation-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{t.valuationPage.notes}</label>
            <Textarea data-testid="valuation-notes-input" rows={3} value={form.notes} onChange={set("notes")} className="bg-white border-sand focus-visible:ring-gold rounded-none text-[15px]" />
          </div>
        </div>
      </div>

      <button
        data-testid="valuation-submit-btn"
        type="submit"
        disabled={sending}
        className="group w-full inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-semibold text-sm tracking-wide px-8 py-4 transition-colors duration-300"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t.valuationPage.cta}
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};
